/**
 * @file        concurrency.js
 *              Concurrency primitives for DCP.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        Dec 2020
 */
'use strict';

const inspect = Symbol.for('nodejs.util.inspect.custom');
const debugging = require('../debugging').scope('concurrency');
const { EventEmitter }      = require('events');
const { DCPError }          = require('dcp/common/dcp-error');
const { assert, assertHas } = require('dcp/common/dcp-assert');
const { requireNative }     = require('dcp/dcp-client/webpack-native-bridge');
const { performance }       = globalThis.performance ? globalThis : requireNative('perf_hooks');
const { Inventory }         = require('../utils/inventory');

/**
 * Synchronizer constructor. Instance objects are used to accurately manage
 * transitions between various states. Observing calls to set (possibly via testAndSet
 * or setIf) can allow a developer to develop an accurate state transition diagram.
 * Every mutation of a Synchronizer's state causes the lastStack and lastChange properties
 * be updated. These may be safely used by API consumers. LastStack is a string, and
 * lastChange is in milliseconds, from a monotonic timer that began when the process launched.
 *
 * @param {any}         initial         initial value of the synchronizer
 * @param {Array}       assertValues    [optional] if present, assert that all set values are in this
 *                                      array when running a debug build
 */
function Synchronizer(initial, assertValues)
{
  this._ = initial; /* current/internal state of the synchronizer is stored on this._ */
  this.lastStack = new Error().stack;
  this.lastChanged = performance.now();
  this.promiseRegistry = new Inventory(); /* promises which will need cleanup upon destroy() */
  this.setMaxListeners(20); /* max listeners affects the number of "threads" that can spin on until() without a warning */

  if (!assertValues)
    return;

  for (let av of assertValues)
    assert(typeof av !== 'undefined');

  this.assertValues = [].concat(assertValues);
  assertHas(this.assertValues, this._);
}
exports.Synchronizer = Synchronizer; // Allows VSCode to see the members of Synchronizer.
Synchronizer.prototype = Object.create(EventEmitter.prototype);
Synchronizer.prototype.constructor = Synchronizer;
Synchronizer.prototype[inspect] = function ()
{
  return `[Object Synchronizer <${this._}>]`;
};

/**
 * Factory function which makes a new Synchronizer that has the same current value as this
 * synchronizer and the same assertValues (if applicable), but not the same event listeners.
 */
Synchronizer.prototype.duplicate = function concurrency$$Synchronizer$duplicate()
{
  return new Synchronizer(this._, this.assertValues);
}

/**
 * Set the synchronizer a given state.
 *
 * @param       old     The state the synchronizer is currently in
 * @param       neu     The new state in which to place the synchronizer
 * @throws code DCP_SYNCHRONIZER_ESYNC if the synchronizer was not in the old state
 */
Synchronizer.prototype.set = function concurrency$$Synchronizer$set(old, neu)
{
  const that = this;
  assert(typeof old !== 'undefined', typeof neu !== 'undefined');

  /* Assertions in here 
   *  a) ensure the new state is a valid state
   *  b) check make sure we are not trying to re-set the current state, but
   *  c) allow resetting of the current state when the old state is a list of
   *     of allowable states. eg.
   *       set(x,x)       - illegal
   *       set([x],x)     - illegal
   *       set([x, y], x) - legal
   */
  if (!Array.isArray(old))
    assert(old !== neu);
  else
  {
    let idx = old.indexOf(this._);
    if (idx === -1)
      throwup();

    if (old.length === 1)
      assert(old.indexOf(neu) === -1);
    for (let state of old)
      assertHas(this.assertValues, state);

    old = old[idx];
  }

  if (this._ !== old)
    throwup();

  this._ = neu;
  this.lastStack = new Error().stack;
  this.lastChanged = performance.now();
  assertHas(this.assertValues, this._);
  this.emit('change', this._, old);

  function throwup()
  {
    if (!that.lastStack)
      throw new DCPError(`Cannot transition synchronizer from ${old}->${neu}; was in ${that._}`, 'DCP_SYNCHRONIZER_ESYNC');
    else
    {
      let lastCaller = that.lastStack.split('\n')[2].replace(/^  *at /, '');
      throw new DCPError(`Cannot transition synchronizer from ${old}->${neu}; was in ${that._} from ${lastCaller}`, 'DCP_SYNCHRONIZER_ESYNC');
    }
  }
}

/**
 * Returns a promise which resolves when this Synchronizer achieves the desired state, or
 * rejects when the Synchronizer is destroyed.
 *
 * @param   {any}       which   The state we are waiting for
 * @returns {promise}
 */
Synchronizer.prototype.until = function concurrency$$Synchronizer$until(which)
{
  var that = this;
  var pHnd = { stack: debugging('until') && new Error('stackMemo').stack };

  function pWrapper(__resolve, __reject)
  {
    pHnd.resolve = (...args) => {
      __resolve(...args);
      if (that.promiseRegistry.includes(pHnd))
        that.promiseRegistry.delete(pHnd);
    };
    pHnd.reject = (...args) => {
      __reject(...args);
      if (that.promiseRegistry.includes(pHnd))
        that.promiseRegistry.delete(pHnd);
    };

    function changeHandlerOne(newState)
    {
      if (newState !== which || that._ !== which)
        return;

      that.removeListener('change',  changeHandlerOne);
      pHnd.resolve();
    }

    function changeHandlerMany(newState)
    {
      if (which.indexOf(newState) === -1 && which.indexOf(that._) === -1)
        return;

      that.removeListener('change',  changeHandlerMany);
      that.promiseRegistry.delete(pHnd);
      pHnd.resolve();
    }

    that.on('change',  Array.isArray(which) ? changeHandlerMany : changeHandlerOne);
  }

  pHnd.promise = new Promise(pWrapper);
  that.promiseRegistry.add(pHnd);

  if (that._ === which)
    pHnd.resolve();

  return pHnd.promise;
}

/**
 * Destroy a synchronizer. Once this function has been invoked, no further operations on
 * the synchronizer are permitted.
 *
 * Causes subsequent attempts to read or write the interval state to throw code DCP_SYNCHRONIZER_EINVAL .
 */
Synchronizer.prototype.destroy = function concurrency$$Synchronizer$destroy()
{
  Object.defineProperty(this, '_', {
    get: () => { throw new DCPError('synchronizer has been destroyed', 'DCP_SYNCHRONIZER_EINVAL'); },
    set: () => { throw new DCPError('synchronizer has been destroyed', 'DCP_SYNCHRONIZER_EINVAL'); },

    configurable: false,
    enumerable:   false,
  });

  this.promiseRegistry.forEach(pHnd => pHnd.reject());
  this.emit('destroy');
  this.removeAllListeners();
  assert(this.promiseRegistry.length === 0);
}

/**
 * Set the synchronizer instance to a given state, providing it was previously in
 * the supplied 'old' state
 *
 * @param       old     The state the synchronizer is currently in
 * @param       neu     The new state in which to place the synchronizer
 *
 * @returns true if the synchronizer state was changed, otherwise false
 */
Synchronizer.prototype.testAndSet = function concurrency$$Synchronizer$testAndSet(old, neu)
{
  assertHas(this.assertValues, old);
  assertHas(this.assertValues, neu);

  if (this._ !== old)
    return false;

  this.set(old, neu);

  return true;
}
Synchronizer.prototype.setIf = Synchronizer.prototype.testAndSet;

/**
 * Test to see if the synchronizer is in a given state.
 * @param {any}         test    the state we're checking
 *
 * @returns {boolean} true if synchronizer was in the test state
 */
Synchronizer.prototype.test = function concurrency$$Synchronizer$test(test) {
  assertHas(this.assertValues, test);
  return test === this._;
}
Synchronizer.prototype.is = Synchronizer.prototype.test;

Synchronizer.prototype.isNot = function concurrency$$Synchronizer$isNot(test)
{
  assertHas(this.assertValues, test);
  return test !== this._;
}

Synchronizer.prototype.in = function concurrency$$Synchronizer$in(list) {
  for (let item of list)
    assertHas(this.assertValues, item);

  return list.indexOf(this._) !== -1;
}

/**
 * `valueOf` functionality for the synchronizer; provides the value of the
 * underlying current state; can be used for equality comparisons, etc.
 */
Synchronizer.prototype.valueOf = function concurrency$$Synchronizer$valueOf() {
  return this._.valueOf();
}
Synchronizer.prototype.toString = Synchronizer.prototype.valueOf;

/**
 * Atomic (from a JS POV) test-and-set operation. Sets `obj[key] = neu` when `obj[keu] === old`.
 *
 * @param {object} obj
 * @param {string} key
 * @param {any}    old
 * @param {any}    new
 * @returns {any} the value of obj[key] when the function was invoked
 */
exports.testAndSet = function concurrency$$compareAndSwap(obj, key, old, neu)
{
  const initial = obj[key];

  if (initial === old)
    obj[key] = neu;
  return initial;
}

const fifos = {};

/**
 * Run critical functions in series; FIFO order. If a function is running, we spin until it is done
 * before running a new function.
 *
 * @param {String|Symbol}   fifoHnd            a handle uniquely describing a given FIFO in this process
 * @param {function}        criticalFunction   the function to run in series
 *
 * @returns {Promise} which resolves or rejects to match the behavior of criticalFunction.
 */
exports.fifoMutex = async function(fifoHnd, criticalFunction)
{
  const fifo = fifos[fifoHnd] = (fifos[fifoHnd] || []);
  fifo.unshift({});
  fifo[0].promise = new Promise(resolve => {
    fifo[0].resolve = resolve;
  });

  if (fifo.length > 1)
    await fifo[1].promise;

  try
  {
    return await criticalFunction();
  }
  finally
  {
    fifo.pop().resolve();
  }
}
